import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ClipboardCheck,
  PackageCheck,
  RefreshCw,
  ScanLine,
  Tag,
  X,
} from 'lucide-react';
import { api, ErroHttp } from '@/lib/api';
import { useContexto } from '@/hooks/useContexto';
import type { CentroContexto } from '@/lib/tipos';
import { useCracha } from '@/hooks/useCracha';
import { PedirCracha, SeloCracha } from '@/componentes/operacao/Cracha';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { CabecalhoPagina, Esqueleto, Pilula } from '@/componentes/ui/primitivos';
import { ImagemProduto } from '@/componentes/produto/ImagemProduto';

/**
 * Conferência — a etapa entre separar e faturar.
 *
 * A regra de quem pode ser conferido é do formulário `CONFERENCIAS_PEDIDOS_VENDAS`
 * do Procfit: **estado 3 (Em Separação)** e sem conferência anterior. Não é a
 * etiqueta que libera — `ETIQUETAS_SEPARACAO.STATUS` não entra na conta.
 *
 * O gesto é um PAR por peça:
 *
 *   1. bipa a **etiqueta de separação** — uma para CADA peça, mesmo que o
 *      pedido tenha três unidades do mesmo SKU;
 *   2. bipa o **produto** daquela etiqueta (EAN, DUN, SKU ou número de série),
 *      e ele tem de ser o produto que a etiqueta diz.
 *
 * Repete até 100%. Não fecha com nada faltando.
 *
 * **A conferência é CEGA.** A tela não mostra o que se espera encontrar: nem
 * SKU, nem descrição, nem quantidade por item. O risco aqui não é o operador
 * errar — é ele acertar sem olhar a caixa: com a lista à vista, basta bipar o
 * EAN de um catálogo ou de outra peça igual na bancada e o pedido "fecha" sem
 * que ninguém tenha verificado o conteúdo. Por isso os itens nem saem da API
 * (`montarConferencia(televenda, cego)`): dado que não trafega não aparece no
 * inspetor do navegador.
 *
 * O que ele PRECISA saber, e vê: **quanto falta**. Cego é sobre o quê, não
 * sobre quanto — sem isso ninguém sabe quando parar.
 *
 * As validações seguem o padrão do Procfit: a regra é um SELECT que, se
 * devolver linha, trava, e a linha é a própria mensagem. A tela mostra a frase
 * que a regra escreveu.
 *
 * Somente leitura: fechar a conferência muda o pedido para o estado 28 e
 * libera faturamento — passo à parte, ainda não ligado.
 */

interface PedidoConferencia {
  televenda: number;
  empresa: number | null;
  estado: string | null;
  estadoDescricao: string | null;
  cliente: string | null;
  uf: string | null;
  cidade: string | null;
  transporte: string | null;
  parceiro: string | null;
  pedidoMarketplace: string | null;
  bloqueio: string | null;
  conferenciaExistente: number | null;
  /** O alvo: quantas etiquetas o pedido tem. */
  etiquetas: number;
  /**
   * O alvo de verdade: quantas PEÇAS o pedido pede.
   *
   * Não a contagem de etiquetas. Um pedido de 2 unidades do mesmo SKU pede 2
   * peças e pode ter 2 etiquetas, 1 ou nenhuma — contar etiqueta fazia a barra
   * fechar antes da hora, ou nunca fechar.
   */
  pecas: number;
}

interface EtiquetaBipada {
  etiqueta: number;
  televenda: number;
  produto: number;
  descricao: string | null;
  ean: string | null;
  endereco: string | null;
  bloqueio: string | null;
}

interface PedidoPendente {
  televenda: number;
  cliente: string | null;
  estado: string | null;
  transporte: string | null;
  pecas: number;
  etiquetas: number;
  skus: number;
}

interface Fila {
  empresa: number;
  pendentes: PedidoPendente[];
  foraDoEstado: { estado: string; descricao: string | null; pedidos: number }[];
}

/**
 * A resposta do bipe da etiqueta quando o código NÃO era uma etiqueta.
 *
 * É diferente de uma recusa: a etiqueta pode não existir porque o operador
 * bipou o EAN da peça, que na conferência manual é um caminho legítimo. Sem
 * este sinal a tela só sabia dizer "código não corresponde a nenhuma etiqueta".
 */
interface RecusaDeEtiqueta {
  bloqueio: string;
  naoEhEtiqueta?: boolean;
}

/** Uma peça já conferida nesta sessão: a etiqueta e o produto que a confirmou. */
interface Conferida {
  /** Chave de lista — a etiqueta não serve, pelo EAN direto ela não existe. */
  chave: number;
  /** `null` quando a peça foi conferida pelo EAN, sem etiqueta. */
  etiqueta: number | null;
  produto: number;
  /** Quantas unidades este bipe conferiu. */
  quantidade: number;
  /** O código como foi bipado — é ele que vai para o ERP em `CODIGO_BARRAS`. */
  codigo: string;
  descricao: string | null;
  endereco: string | null;
  origem: string;
}

interface Aviso {
  tom: 'ok' | 'erro';
  texto: string;
}

export function Conferencia(): JSX.Element {
  const { empresa, centroAtual, centrosDaEmpresa } = useContexto();
  /*
    O CENTRO DE ESTOQUE DA CONFERÊNCIA.

    Vai em CENTRO_ESTOQUE de cada item gravado, e o faturamento não emite com
    ele nulo. Padrão: o centro da sessão. O operador pode trocar por outro
    centro da empresa selecionada — há quem confira pedido de outro centro na
    mesma bancada. Sem centro, não fecha.
  */
  const [centroConferencia, definirCentroConferencia] = React.useState<number | null>(
    centroAtual?.centroEstoque ?? null,
  );
  React.useEffect(() => {
    definirCentroConferencia(centroAtual?.centroEstoque ?? null);
  }, [centroAtual?.centroEstoque]);
  /*
    O crachá assina a conferência.

    É ele que vai para `CONFERENCIAS_PEDIDOS_VENDAS.CRACHA` e para a fila de
    faturamento (`FATURAMENTO_STATUS.CRACHA`) — o ERP identifica a operação
    por crachá, não por login. Vem do cadastro quando há vínculo; na falta, a
    pessoa informa e o número fica só no navegador dela.
  */
  const cracha = useCracha();
  const campoRef = React.useRef<HTMLInputElement | null>(null);

  const [codigo, definirCodigo] = React.useState('');
  const [televenda, definirTelevenda] = React.useState<number | null>(null);
  /** A etiqueta bipada esperando o produto confirmar. */
  const [pendente, definirPendente] = React.useState<EtiquetaBipada | null>(null);
  const [conferidas, definirConferidas] = React.useState<Conferida[]>([]);
  const [aviso, definirAviso] = React.useState<Aviso | null>(null);
  const [ocupado, definirOcupado] = React.useState(false);
  const [fechando, definirFechando] = React.useState(false);
  /**
   * Quantas unidades o próximo bipe vale.
   *
   * Existe para o caso de a televenda pedir 2 do mesmo SKU: em vez de bipar a
   * mesma peça duas vezes, digita-se 2 e bipa-se uma. Volta a 1 sozinho depois
   * de cada bipe — um campo de quantidade que fica preso no valor anterior é
   * como conferência a mais entra sem ninguém perceber.
   */
  const [quantidade, definirQuantidade] = React.useState(1);
  const proximaChave = React.useRef(0);

  const fila = useQuery<Fila>({
    queryKey: ['conferencia', 'pendentes', empresa],
    queryFn: () => api.get<Fila>('/api/conferencia/pendentes', { empresa }),
    enabled: empresa !== null,
    staleTime: 30_000,
  });

  const pedido = useQuery<PedidoConferencia>({
    queryKey: ['conferencia', 'pedido', televenda],
    queryFn: () => api.get<PedidoConferencia>('/api/conferencia/pedido', { codigo: televenda }),
    enabled: televenda !== null,
    staleTime: 10_000,
  });

  const p = pedido.data ?? null;
  const alvo = p?.pecas ?? 0;
  const feitas = conferidas.reduce((soma, c) => soma + c.quantidade, 0);
  const falta = Math.max(0, alvo - feitas);
  const completo = alvo > 0 && falta === 0;

  const focar = React.useCallback(() => campoRef.current?.focus(), []);
  React.useEffect(() => {
    focar();
  }, [focar, televenda, pendente]);

  const limpar = (): void => {
    definirQuantidade(1);
    definirTelevenda(null);
    definirPendente(null);
    definirConferidas([]);
    definirAviso(null);
    focar();
  };

  const abrir = (numeroDoPedido: number): void => {
    definirQuantidade(1);
    definirTelevenda(numeroDoPedido);
    definirPendente(null);
    definirConferidas([]);
    definirAviso(null);
  };

  /*
   * Segunda barreira, agora sobre o pedido JÁ carregado.
   *
   * A primeira barra na abertura por bipe. Esta pega o caso em que o estado
   * mudou depois de aberto (outra pessoa faturou, cancelou, conferiu) — o
   * pedido some da fila, mas quem está com ele na tela não saberia.
   */
  const bloqueado = p?.bloqueio != null;

  /**
   * Confere uma peça pelo PRODUTO, sem etiqueta.
   *
   * Vale para o EAN, o DUN, o SKU e o número de série — `Coletor_RetornarProduto`
   * resolve todos. O que não dá para deixar a tela decidir é se aquele produto
   * serve: a conferência é cega, o navegador não recebe os itens do pedido e
   * portanto não teria como comparar. Por isso a rota devolve `bloqueio` e a
   * tela só repete a frase.
   *
   * `sessao` são os SKUs já bipados aqui e ainda não gravados. Sem eles a trava
   * de quantidade não vê nada: a conferência só vira linha no banco quando
   * fecha, então bipar cinco vezes o mesmo EAN num pedido que pede um passaria
   * — que é exatamente o buraco que a etiqueta tapava sozinha.
   */
  const conferirPeloProduto = async (codigoDoProduto: string): Promise<void> => {
    if (televenda === null) return;

    const r = await api.get<{
      produto?: number;
      descricao?: string | null;
      origem?: string;
      bloqueio: string | null;
    }>('/api/conferencia/produto', {
      codigo: codigoDoProduto,
      televenda,
      quantidade,
      /* A sessão vai repetida por unidade: o servidor conta ocorrências do SKU
         que o código resolver, e é assim que 2 un contam como 2. */
      sessao: conferidas.flatMap((c) => Array<number>(c.quantidade).fill(c.produto)).join(','),
    });

    if (r.bloqueio || !r.produto) {
      definirAviso({
        tom: 'erro',
        texto: r.bloqueio ?? 'Este código não é uma etiqueta nem um produto conhecido.',
      });
      return;
    }

    definirConferidas((atual) => [
      {
        chave: (proximaChave.current += 1),
        etiqueta: null,
        produto: r.produto!,
        quantidade,
        codigo: codigoDoProduto,
        descricao: r.descricao ?? null,
        endereco: null,
        origem: r.origem ?? 'código',
      },
      ...atual,
    ]);
    definirAviso({
      tom: 'ok',
      texto:
        quantidade === 1
          ? `Conferida pelo produto ${r.produto}.`
          : `${quantidade} un conferidas pelo produto ${r.produto}.`,
    });
    definirQuantidade(1);
  };

  /** Tira uma linha conferida por engano. Só mexe na tela — nada foi gravado. */
  const removerConferida = (chave: number): void => {
    definirConferidas((atual) => atual.filter((c) => c.chave !== chave));
    definirAviso(null);
    focar();
  };

  /**
   * FINALIZAR — o único gesto desta tela que grava no ERP.
   *
   * Só existe em 100%: conferência com peça faltando não é conferência
   * parcial, é conferência não terminada. E o servidor refaz a conta inteira
   * de qualquer jeito — o botão desabilitado é cortesia com quem opera, não a
   * trava. A trava é lá, porque aqui a tela é cega e não tem como saber.
   */
  const finalizar = async (): Promise<void> => {
    if (televenda === null || fechando || !completo) return;
    if (centroConferencia === null) {
      definirAviso({ tom: 'erro', texto: 'Escolha o centro de estoque da conferência.' });
      return;
    }
    definirFechando(true);
    try {
      const r = await api.post<{ bloqueio: string | null; conferencia?: number }>(
        '/api/conferencia/fechar',
        {
          televenda,
          centro: centroConferencia,
          /* Só quando NÃO veio do cadastro: crachá da sessão o servidor já
             tem, e aceitar o do corpo por cima seria deixar assinar por outro. */
          ...(cracha.daSessao ? {} : { cracha: cracha.cracha }),
          itens: conferidas.map((c) => ({
            produto: c.produto,
            quantidade: c.quantidade,
            codigoBarras: c.codigo,
            etiqueta: c.etiqueta,
          })),
        },
      );

      if (r.bloqueio) {
        definirAviso({ tom: 'erro', texto: r.bloqueio });
        return;
      }

      definirTelevenda(null);
      definirPendente(null);
      definirConferidas([]);
      definirQuantidade(1);
      definirAviso({
        tom: 'ok',
        texto: `Conferência ${r.conferencia} gravada no ERP. O pedido está liberado para faturar.`,
      });
      void fila.refetch();
    } catch (erro) {
      definirAviso({
        tom: 'erro',
        texto: erro instanceof ErroHttp ? erro.message : 'Nao foi possivel gravar a conferencia.',
      });
    } finally {
      definirFechando(false);
      focar();
    }
  };

  /**
   * O bipe, em três situações que o estado da tela decide sozinho — o operador
   * não escolhe modo nenhum, só aponta o leitor.
   */
  const bipar = async (valor: string): Promise<void> => {
    const limpo = valor.trim();
    if (!limpo || ocupado) return;
    definirOcupado(true);
    definirCodigo('');

    try {
      // 1. Sem pedido aberto: o código abre o pedido.
      if (televenda === null) {
        const aberto = await api.get<PedidoConferencia>('/api/conferencia/pedido', {
          codigo: limpo,
        });

        /*
         * Pedido bloqueado NÃO ABRE — nem para conferir "só para ver".
         *
         * É o que o ERP faz: a validação do campo TELEVENDA recusa na porta, e
         * o formulário nem chega a montar. Eu tinha deixado abrir com uma
         * tarja de aviso, o que é pior do que parece: permite conferir um
         * pedido já faturado até 100%, dar tudo verde, e só no fim descobrir
         * que aquilo não valia nada. Trabalho perdido, e pior — confiança
         * perdida no verde da tela.
         */
        if (aberto.bloqueio) {
          definirAviso({ tom: 'erro', texto: aberto.bloqueio });
          return;
        }

        abrir(aberto.televenda);

        /*
         * A etiqueta que abriu o pedido JÁ É a primeira peça.
         *
         * Ela carrega as duas informações: qual pedido e qual SKU. Pedir para
         * bipar a MESMA etiqueta de novo, só porque a primeira leitura serviu
         * para identificar o pedido, é um ciclo inteiro de trabalho jogado
         * fora — e a primeira coisa que o conferente faria seria achar que o
         * leitor não pegou.
         *
         * Se o operador digitou a televenda em vez de bipar, a validação
         * recusa e simplesmente ficamos esperando a primeira etiqueta.
         */
        const comoEtiqueta = await api
          .get<EtiquetaBipada | { bloqueio: string }>('/api/conferencia/etiqueta', {
            codigo: limpo,
            televenda: aberto.televenda,
          })
          .catch(() => null);

        if (comoEtiqueta && !comoEtiqueta.bloqueio) {
          const etiqueta = comoEtiqueta as EtiquetaBipada;
          definirPendente(etiqueta);
          definirAviso({
            tom: 'ok',
            texto: `Pedido ${aberto.televenda} · etiqueta ${etiqueta.etiqueta}. Agora bipe o produto.`,
          });
          return;
        }

        definirAviso({ tom: 'ok', texto: `Pedido ${aberto.televenda} aberto. Bipe a etiqueta.` });
        return;
      }

      if (bloqueado) {
        definirAviso({ tom: 'erro', texto: p?.bloqueio ?? 'Este pedido não pode ser conferido.' });
        return;
      }

      /*
       * 2. Com pedido aberto e sem etiqueta pendente: ETIQUETA — ou o EAN da
       *    peça direto.
       *
       * Os dois caminhos chegam ao mesmo SKU, que é o que a conferência quer
       * saber. A etiqueta é o caminho da onda: ela diz também DE ONDE a peça
       * saiu, e por isso continua sendo o primeiro palpite. Mas nem toda peça
       * chega à bancada com etiqueta legível — e na conferência manual não há
       * etiqueta nenhuma. Obrigar a bipar uma era travar a conferência num
       * papel que não existe.
       *
       * Quem decide não é o operador escolhendo um modo: é o próprio código.
       * Se o servidor responde que aquilo não é etiqueta, a tela tenta o
       * produto — as duas travas (peça fora do pedido e quantidade acima do
       * pedido) moram lá e valem para os dois caminhos.
       */
      if (pendente === null) {
        const r = await api.get<EtiquetaBipada | RecusaDeEtiqueta>('/api/conferencia/etiqueta', {
          codigo: limpo,
          televenda,
        });

        if (!r.bloqueio) {
          const etiqueta = r as EtiquetaBipada;
          if (conferidas.some((c) => c.etiqueta === etiqueta.etiqueta)) {
            definirAviso({ tom: 'erro', texto: 'Esta etiqueta já foi conferida agora.' });
            return;
          }
          definirPendente(etiqueta);
          definirAviso({ tom: 'ok', texto: `Etiqueta ${etiqueta.etiqueta}. Agora bipe o produto.` });
          return;
        }

        /* Recusa de verdade (etiqueta de outro pedido, já conferida…) para
           aqui: insistir no produto trocaria a frase certa por outra. */
        if (!(r as RecusaDeEtiqueta).naoEhEtiqueta) {
          definirAviso({ tom: 'erro', texto: r.bloqueio });
          return;
        }

        await conferirPeloProduto(limpo);
        return;
      }

      // 3. Com etiqueta pendente: o código é o PRODUTO dela.
      const achado = await api.get<{
        produto?: number;
        origem?: string;
        bloqueio: string | null;
      }>('/api/conferencia/produto', { codigo: limpo, televenda });

      // A recusa vem da procedure do ERP, com a frase dela.
      if (achado.bloqueio) {
        definirAviso({ tom: 'erro', texto: achado.bloqueio });
        return;
      }
      if (achado.produto !== pendente.produto) {
        definirAviso({
          tom: 'erro',
          texto: `Produto bipado (${achado.produto}) nao e o da etiqueta ${pendente.etiqueta}.`,
        });
        return;
      }

      definirConferidas((atual) => [
        {
          chave: (proximaChave.current += 1),
          etiqueta: pendente.etiqueta,
          produto: pendente.produto,
          /* Pela etiqueta a quantidade é sempre 1: existe uma etiqueta por
             peça, e é isso que a torna confiável. O campo de quantidade serve
             ao bipe do produto, onde não há etiqueta contando por nós. */
          quantidade: 1,
          codigo: String(pendente.etiqueta),
          descricao: pendente.descricao,
          endereco: pendente.endereco,
          origem: achado.origem ?? 'código',
        },
        ...atual,
      ]);
      definirPendente(null);
      definirAviso({ tom: 'ok', texto: `Conferida (por ${achado.origem ?? 'código'}).` });
    } catch (erro) {
      definirAviso({
        tom: 'erro',
        texto: erro instanceof ErroHttp ? erro.message : 'Nao foi possivel ler este codigo.',
      });
    } finally {
      definirOcupado(false);
      focar();
    }
  };

  if (empresa === null) {
    return (
      <EstadoVazio
        titulo="Escolha um centro de estoque"
        detalhe="A fila de conferência é por empresa, e a empresa vem do centro ativo."
      />
    );
  }

  const esperando: 'pedido' | 'etiqueta' | 'produto' =
    televenda === null ? 'pedido' : pendente === null ? 'etiqueta' : 'produto';

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-5">
      <CabecalhoPagina
        icone={<ClipboardCheck className="h-5 w-5" />}
        sobrelinha={
          centroAtual
            ? `Operação · ${centroAtual.centroEstoque} ${centroAtual.nome}`
            : `Operação · empresa ${empresa}`
        }
        titulo="Conferência"
        acoes={
          <span className="flex items-center gap-2">
            {/* O selo fica SEMPRE visível, com ou sem pedido aberto: quem vai
                assinar precisa ver qual crachá está assinando antes de começar
                a bipar, não no momento de gravar. */}
            <SeloCracha cracha={cracha} />
            {televenda !== null ? (
              <>
                <Pilula tom="neutro" title="A tela não mostra o que se espera encontrar">
                  cega
                </Pilula>
                <Botao variante="secundaria" tamanho="pequeno" onClick={limpar}>
                  Trocar de pedido
                </Botao>
              </>
            ) : null}
          </span>
        }
      />

      {cracha.cracha === null ? (
        <PedirCracha
          aoInformar={cracha.informar}
          motivo="É o crachá que assina a conferência no ERP e põe o pedido na fila de faturamento."
        />
      ) : null}

      {/* O CAMPO: é o gesto inteiro, e diz sozinho o que espera agora. */}
      <form
        className={cn(
          'cartao flex items-center gap-3 px-4 py-3 transition-colors',
          esperando === 'produto' && 'ring-2 ring-primaria',
        )}
        onSubmit={(e) => {
          e.preventDefault();
          void bipar(codigo);
        }}
      >
        {esperando === 'produto' ? (
          <PackageCheck className="h-6 w-6 shrink-0 text-primaria" />
        ) : esperando === 'etiqueta' ? (
          <Tag className="h-6 w-6 shrink-0 text-primaria" />
        ) : (
          <ScanLine className="h-6 w-6 shrink-0 text-primaria" />
        )}

        {/* A quantidade do PRÓXIMO bipe. Só aparece quando o próximo bipe é um
            produto — pela etiqueta a contagem é uma por peça, e um campo de
            quantidade ali só serviria para desmentir a etiqueta. */}
        {esperando === 'etiqueta' ? (
          <label
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-borda bg-superficie2 px-2 py-1"
            title="Quantas unidades o próximo bipe vale"
          >
            <span className="rotulo">qtde</span>
            <input
              type="number"
              min={1}
              value={quantidade}
              onChange={(e) => definirQuantidade(Math.max(1, Math.trunc(Number(e.target.value) || 1)))}
              /* Enter na quantidade devolve o leitor, que é onde o bipe cai. */
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  focar();
                }
              }}
              aria-label="Quantidade do próximo bipe"
              className="tabular w-12 bg-transparent text-center font-mono text-[15px] font-bold outline-none"
            />
          </label>
        ) : null}

        <input
          ref={campoRef}
          value={codigo}
          onChange={(e) => definirCodigo(e.target.value)}
          /*
            O ENTER É TRATADO AQUI, E NÃO PELO `submit` IMPLÍCITO DO FORM.

            O leitor de código termina cada bipe com Enter, e enquanto este era
            o único campo do formulário o navegador submetia sozinho. Ao ganhar
            o campo de quantidade o form passou a ter DOIS campos de texto e
            nenhum botão de submit — e nesse caso o HTML manda ignorar o Enter.
            Resultado medido: o código ficava parado no campo e o contador em
            0/2, como se o leitor não tivesse pegado.
          */
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void bipar(codigo);
            }
          }}
          /* Devolve o foco ao leitor, MENOS quando ele está indo para o campo
             de quantidade — senão não dava para digitar lá. */
          onBlur={(e) => {
            const indo = e.relatedTarget as HTMLElement | null;
            if (!indo || !e.currentTarget.form?.contains(indo)) focar();
          }}
          autoFocus
          placeholder={
            cracha.cracha === null
              ? 'informe o seu crachá acima para começar'
              : esperando === 'pedido'
              ? 'bipe a etiqueta de separação ou digite a televenda'
              : esperando === 'etiqueta'
                ? 'bipe a ETIQUETA ou o produto da próxima peça'
                : `bipe o PRODUTO da etiqueta ${pendente?.etiqueta ?? ''}`
          }
          aria-label="Leitor"
          /* Sem crachá o leitor não abre. Deixar bipar e recusar no fim seria
             desperdiçar a conferência inteira de quem não tem vínculo — o
             cartão logo acima pede o número, e é a primeira coisa a fazer. */
          disabled={cracha.cracha === null}
          className="tabular min-h-[44px] flex-1 bg-transparent font-mono text-[18px] outline-none placeholder:font-sans placeholder:text-[14px] placeholder:text-textoSuave disabled:cursor-not-allowed"
        />

        {televenda !== null ? (
          <span className="shrink-0 text-right leading-none">
            <b
              className={cn(
                'tabular text-[26px] font-bold',
                completo ? 'text-status-livre' : 'text-primaria',
              )}
            >
              {numero(feitas)}
            </b>
            <span className="text-[16px] text-textoFraco">/{numero(alvo)}</span>
            <span className="rotulo mt-1 block">peças</span>
          </span>
        ) : null}
      </form>

      {aviso ? (
        <p
          className={cn(
            'flex items-center gap-2 rounded-xl px-4 py-2.5 text-[13.5px] font-semibold',
            aviso.tom === 'ok'
              ? 'bg-status-livreFundo text-status-livre'
              : 'bg-status-divergenciaFundo text-status-divergencia',
          )}
        >
          {aviso.tom === 'ok' ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertTriangle className="h-4 w-4" />
          )}
          {aviso.texto}
        </p>
      ) : null}

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[300px_minmax(0,1fr)]">
        <FilaPendentes fila={fila} aoAbrir={abrir} televendaAberta={televenda} />

        <div className="cartao flex min-h-0 flex-col overflow-hidden p-0">
          {televenda === null ? (
            <div className="grid h-full place-items-center p-10">
              <EstadoVazio
                icone={<ScanLine className="h-7 w-7" />}
                titulo="Bipe a etiqueta do pedido"
                detalhe="O código da etiqueta de separação abre o pedido dela. Também dá para digitar a televenda ou escolher um da fila ao lado."
              />
            </div>
          ) : pedido.isLoading ? (
            <div className="space-y-2 p-3">
              <Esqueleto className="h-16" />
              <Esqueleto className="h-16" />
            </div>
          ) : pedido.isError ? (
            <EstadoErro erro={pedido.error} aoTentarDeNovo={() => void pedido.refetch()} />
          ) : p ? (
            <PedidoAberto
              pedido={p}
              pendente={pendente}
              conferidas={conferidas}
              falta={falta}
              completo={completo}
              fechando={fechando}
              aoRemover={removerConferida}
              aoFinalizar={() => void finalizar()}
              centro={centroConferencia}
              centros={centrosDaEmpresa}
              aoMudarCentro={definirCentroConferencia}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function PedidoAberto({
  pedido,
  pendente,
  conferidas,
  falta,
  completo,
  fechando,
  aoRemover,
  aoFinalizar,
  centro,
  centros,
  aoMudarCentro,
}: {
  pedido: PedidoConferencia;
  pendente: EtiquetaBipada | null;
  conferidas: Conferida[];
  falta: number;
  completo: boolean;
  fechando: boolean;
  aoRemover: (chave: number) => void;
  aoFinalizar: () => void;
  centro: number | null;
  centros: CentroContexto[];
  aoMudarCentro: (centro: number | null) => void;
}): JSX.Element {
  return (
    <>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-borda px-4 py-2.5">
        <span className="tabular font-mono text-[17px] font-bold">{pedido.televenda}</span>
        <span className="text-[13px]">{pedido.cliente ?? '—'}</span>
        <span className="text-[12px] text-textoFraco">
          {pedido.cidade ?? '—'}/{pedido.uf ?? '—'} · {pedido.transporte ?? '—'}
        </span>
        {pedido.estado ? (
          <Pilula tom={pedido.estado === '3' ? 'ok' : 'alerta'} className="ml-auto">
            estado {pedido.estado} · {pedido.estadoDescricao ?? ''}
          </Pilula>
        ) : null}
      </div>

      {pedido.bloqueio ? (
        <p className="flex items-start gap-2 border-b border-borda bg-status-atencaoFundo px-4 py-2.5 text-[13px] text-status-atencao">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            <b>Este pedido não pode ser fechado.</b> {pedido.bloqueio}
          </span>
        </p>
      ) : null}

      {/* A etiqueta bipada esperando o produto: é o único "o quê" que a tela
          revela — e só depois de a peça já estar na mão de quem confere. */}
      {pendente ? (
        <div className="flex items-center gap-3 border-b border-borda bg-primaria-suave px-4 py-3">
          <ImagemProduto sku={pendente.produto} tamanho={52} />
          <span className="min-w-0 flex-1 leading-tight">
            <span className="rotulo">etiqueta {pendente.etiqueta} · aguardando o produto</span>
            <span className="block truncate text-[13.5px] font-semibold">
              bipe o produto desta peça
            </span>
            {pendente.endereco ? (
              <span className="block truncate font-mono text-[11px] text-textoFraco">
                separada em {pendente.endereco}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto">
        {conferidas.length === 0 ? (
          <div className="grid h-full place-items-center p-8">
            <EstadoVazio
              icone={<Tag className="h-7 w-7" />}
              titulo="Nada conferido ainda"
              detalhe="Bipe a etiqueta da peça e depois o produto dela — ou o produto direto, se não houver etiqueta. A tela não mostra o que esperar: é conferência cega."
            />
          </div>
        ) : (
          <ul className="grid gap-1.5 p-3">
            {conferidas.map((c) => (
              <li
                key={c.chave}
                className="flex items-center gap-3 rounded-xl border border-status-livre/40 bg-status-livreFundo px-3 py-2"
              >
                <ImagemProduto sku={c.produto} tamanho={44} />
                <span className="min-w-0 flex-1 leading-tight">
                  <span className="block font-mono text-[12.5px] font-bold">
                    {c.produto}
                    <span className="ml-2 text-[10.5px] font-normal text-textoFraco">
                      {c.etiqueta === null ? 'sem etiqueta' : `etiqueta ${c.etiqueta}`} · por{' '}
                      {c.origem}
                    </span>
                  </span>
                  <span className="block truncate text-[12px]">{c.descricao ?? '—'}</span>
                  {c.endereco ? (
                    <span className="block truncate font-mono text-[10.5px] text-textoFraco">
                      {c.endereco}
                    </span>
                  ) : null}
                </span>
                <span className="tabular shrink-0 text-right leading-none">
                  <b className="text-[17px] font-bold text-status-livre">{numero(c.quantidade)}</b>
                  <span className="rotulo mt-0.5 block">un</span>
                </span>
                {/* Desfazer é da tela, não do ERP: enquanto não finaliza, nada
                    foi gravado, e tirar a linha errada custa um clique. */}
                <button
                  type="button"
                  onClick={() => aoRemover(c.chave)}
                  title="Tirar esta linha da conferência"
                  aria-label={`Tirar o produto ${c.produto} da conferência`}
                  className="shrink-0 rounded-lg p-1 text-textoSuave transition-colors hover:bg-status-divergenciaFundo hover:text-status-divergencia"
                >
                  <X className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        className={cn(
          'border-t border-borda px-4 py-3',
          completo ? 'bg-status-livreFundo' : 'bg-superficie2',
        )}
      >
        <div className="flex flex-wrap items-center gap-3">
          {completo ? (
            <p className="flex items-center gap-2 text-[13.5px] font-semibold text-status-livre">
              <Check className="h-5 w-5" />
              Conferência completa — todas as peças bipadas.
            </p>
          ) : (
            <p className="text-[13px] text-textoFraco">
              Faltam <b className="tabular text-texto">{numero(falta)}</b>{' '}
              {falta === 1 ? 'peça' : 'peças'} para completar. Não dá para fechar antes de 100%.
            </p>
          )}

          {/* O botão fica visível o tempo todo, e desabilitado até 100%: um
              botão que aparece do nada no fim não ensina que ele existe, e
              quem opera precisa saber desde o começo onde a conferência
              termina. */}
          <label className="ml-auto flex items-center gap-2 text-[12.5px]">
            <span className="text-textoFraco">Centro de estoque</span>
            <select
              value={centro ?? ''}
              onChange={(e) => aoMudarCentro(e.target.value ? Number(e.target.value) : null)}
              disabled={fechando}
              className={cn(
                'min-h-[36px] rounded-lg border bg-superficie px-2 text-[13px] focus:border-primaria focus:outline-none',
                centro === null ? 'border-status-divergencia' : 'border-borda',
              )}
              title="Vai no CENTRO_ESTOQUE de cada item da conferência. Padrão: o centro da sessão."
            >
              {centro === null ? <option value="">Escolha…</option> : null}
              {centros.map((c) => (
                <option key={c.centroEstoque} value={c.centroEstoque}>
                  {c.centroEstoque} · {c.nome}
                </option>
              ))}
            </select>
          </label>
          <Botao
            onClick={aoFinalizar}
            disabled={!completo || fechando || pedido.bloqueio !== null || centro === null}
            title={
              pedido.bloqueio
                ? 'Este pedido não pode ser fechado.'
                : completo
                  ? 'Grava a conferência no ERP com o seu usuário'
                  : 'Bipe todas as peças do pedido para liberar'
            }
          >
            {fechando ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <PackageCheck className="h-4 w-4" />
            )}
            {fechando ? 'Gravando…' : 'Finalizar conferência'}
          </Botao>
        </div>
      </div>
    </>
  );
}

function FilaPendentes({
  fila,
  aoAbrir,
  televendaAberta,
}: {
  fila: ReturnType<typeof useQuery<Fila>>;
  aoAbrir: (televenda: number) => void;
  televendaAberta: number | null;
}): JSX.Element {
  const dados = fila.data;
  const [busca, definirBusca] = React.useState('');

  const visiveis = React.useMemo(() => {
    const t = busca.trim().toLowerCase();
    const todos = dados?.pendentes ?? [];
    if (!t) return todos;
    return todos.filter(
      (x) => String(x.televenda).includes(t) || (x.cliente ?? '').toLowerCase().includes(t),
    );
  }, [dados, busca]);

  return (
    <div className="cartao flex max-h-full min-h-0 flex-col overflow-hidden p-0">
      <div className="flex items-center gap-2 border-b border-borda px-3 py-2">
        <p className="text-[13px] font-bold">A conferir</p>
        {dados ? (
          <Pilula tom="neutro">
            {busca
              ? `${numero(visiveis.length)}/${numero(dados.pendentes.length)}`
              : numero(dados.pendentes.length)}
          </Pilula>
        ) : null}
        <button
          type="button"
          onClick={() => void fila.refetch()}
          title="Atualizar a fila"
          className="ml-auto rounded-lg p-1 text-textoFraco transition-colors hover:bg-superficie2 hover:text-texto"
        >
          <RefreshCw className={cn('h-3.5 w-3.5', fila.isFetching && 'animate-spin')} />
        </button>
      </div>

      {/*
        A fila é o caminho de EXCEÇÃO, e por isso ganha busca.
        O caminho normal é bipar: a etiqueta já diz qual pedido e qual SKU, e
        procurar numa lista de centenas seria mais lento que ler o código. Ela
        existe para quando a etiqueta rasgou, ou para conferir de onde parou.
      */}
      {(dados?.pendentes.length ?? 0) > 8 ? (
        <div className="border-b border-borda px-2 py-1.5">
          <input
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            placeholder="achar pedido ou cliente"
            aria-label="Procurar na fila"
            className="min-h-[30px] w-full rounded-lg border border-borda bg-superficie px-2 text-[12px]"
          />
        </div>
      ) : null}

      {fila.isLoading ? (
        <div className="space-y-2 p-2">
          <Esqueleto className="h-16" />
          <Esqueleto className="h-16" />
        </div>
      ) : fila.isError ? (
        <EstadoErro erro={fila.error} aoTentarDeNovo={() => void fila.refetch()} />
      ) : (dados?.pendentes.length ?? 0) > 0 && visiveis.length === 0 ? (
        <p className="px-3 py-6 text-center text-[12.5px] text-textoFraco">
          Nenhum pedido da fila com “{busca}”.
        </p>
      ) : (dados?.pendentes.length ?? 0) === 0 ? (
        <div className="p-3">
          <EstadoVazio
            icone={<PackageCheck className="h-7 w-7" />}
            titulo="Nada a conferir nesta empresa"
            detalhe="O ERP só libera conferência no estado 3 (Em Separação)."
          />
          {/* Fila vazia com pedido preso em outro estado é diagnóstico, não
              silêncio: é a diferença entre "acabou" e "tem coisa travada". */}
          {(dados?.foraDoEstado.length ?? 0) > 0 ? (
            <div className="mt-2 rounded-lg bg-superficie2 px-3 py-2">
              <p className="rotulo mb-1">com etiqueta, em outro estado</p>
              <ul className="grid gap-0.5 text-[11.5px] text-textoFraco">
                {dados?.foraDoEstado.slice(0, 6).map((f) => (
                  <li key={f.estado}>
                    <b className="tabular text-texto">{numero(f.pedidos)}</b> em {f.estado} ·{' '}
                    {f.descricao ?? '—'}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto p-1.5">
          {visiveis.map((pp) => {
            const ativo = pp.televenda === televendaAberta;
            return (
              <li key={pp.televenda}>
                <button
                  type="button"
                  onClick={() => aoAbrir(pp.televenda)}
                  className={cn(
                    'mb-1 w-full rounded-lg border px-2.5 py-2 text-left transition-colors',
                    ativo
                      ? 'border-primaria bg-primaria-suave'
                      : 'border-borda bg-superficie hover:bg-superficie2',
                  )}
                >
                  <span className="flex items-baseline justify-between gap-2">
                    <b
                      className={cn(
                        'tabular font-mono text-[13px]',
                        ativo ? 'text-primaria' : 'text-texto',
                      )}
                    >
                      {pp.televenda}
                    </b>
                    {/* Quantas peças, sim; QUAIS, não — a fila também é cega. */}
                    <span className="tabular text-[11px] text-textoFraco">
                      {numero(pp.etiquetas)} pç
                    </span>
                  </span>
                  <span className="block truncate text-[11.5px] text-textoFraco">
                    {pp.cliente ?? '—'}
                  </span>
                  <span className="block truncate text-[10.5px] text-textoSuave">
                    {pp.transporte ?? ''} {pp.estado ? `· ${pp.estado}` : ''}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
